import React, {useEffect, useState} from 'react';
import {
  AppBar,
  Box,
  Button,
  FormControlLabel,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Toolbar,
  Typography,
} from '@mui/material';

import {ArrowDropDown, DarkMode, LightMode} from '@mui/icons-material';
import Sidebar from './components/sidepanel/SideBar.tsx';
import StatusPage from './StatusPage.tsx';
import useExcelToGeoJson from './hooks/useExcelToGeoJson.ts';
import useFetchAllTags from './hooks/useFetchAllTags.ts';
import {VehicleStatus} from './types';
import tagglist from './tempFile/tagglista20260609.csv';
import {useTheme} from '@mui/styles';
import {SoftprioFeatureName} from './utils/feature_list_names.ts';
import {processDataAndCalculateAverages} from './utils/utils.ts';

const StatusPageContainer: React.FC<{
  pointId: string | null;
  toggleLocalTheme: any;
  localTheme: string;
}> = ({toggleLocalTheme, localTheme}) => {
  //
  const [detectors, setDetectors] = useState([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCdc] = useState<string>(''); // State to hold selected CDC
  const [singleProcessedTag, setSingleProcessedTag] = useState<
    VehicleStatus | undefined
  >();
  const [isBackDropOpen, setIsBackDropOpen] = useState<boolean>(false);
  const [submenuAnchorEl, setSubmenuAnchorEl] = useState<null | HTMLElement>(null);
  const [missingTagsSubmenuAnchorEl, setMissingTagsSubmenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const [selectedValueForFunction, setSelectedValueForFunction] =
    useState<string>('SOFTPRIO');
  const [pointGeoJsonDataV, setGeoJsonDataV] = useState(null);
  const [selectedTag] = useState(null);
  const [isFullScreenForTramDetail, setIsFullScreenForTramDetail] =
    useState<Boolean>(false);
  const {data} = useFetchAllTags();
  const {pointsGeoJsonData, dataLoaded, convertExcelToGeoJson} =
    useExcelToGeoJson(tagglist);

  const [processedTags, setProcessedTags] = useState<VehicleStatus[]>();

  const theme = useTheme();

  useEffect(() => {
    convertExcelToGeoJson();
  }, [convertExcelToGeoJson]);

  useEffect(() => {
    if (dataLoaded) {
      setGeoJsonDataV(pointsGeoJsonData);
    }

    if (data) {
      const {distinctObjects} = processDataAndCalculateAverages(data.data);
      setProcessedTags(distinctObjects);
    }
  }, [data, dataLoaded, pointsGeoJsonData]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (value: string | null) => {
    setAnchorEl(null);
    setSubmenuAnchorEl(null);
    setMissingTagsSubmenuAnchorEl(null);
    setMissingTagsSubmenuAnchorEl(null);
    let finalValue = value;
    if (finalValue === 'heatmap') finalValue = 'Värmekarta';
    if (finalValue) setSelectedValueForFunction(finalValue);
  };

  const handleSubmenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setSubmenuAnchorEl(event.currentTarget);
  };

  const handleMissingTagsSubmenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMissingTagsSubmenuAnchorEl(event.currentTarget);
  };

  const handleMissingTagsSubmenuClose = (event: React.MouseEvent<HTMLElement>) => {
    setMissingTagsSubmenuAnchorEl(null);
  };

  const handleSubmenuClose = () => {
    setSubmenuAnchorEl(null);
  };

  const addDetectorData = (newData: any) => {
    setDetectors((prevDetectors) => [...prevDetectors, newData]);
  };
  // const [isSliderChecked, setSliderChecked] = useState(localTheme === ThemeEnum.softpriolight);

  // const onSliderChange = (event) => {
  //   setSliderChecked(event.target.checked);
  //   console.log('Slider toggled:', event.target.checked);
  // };
  return (
    <Stack sx={{width: '100%', height: '100%'}}>
      <AppBar
        position="static"
        sx={{
          backgroundColor: theme.palette.background.default,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Toolbar>
          <Button
            aria-controls="simple-menu"
            aria-haspopup="true"
            onClick={handleClick}
            endIcon={<ArrowDropDown />}
            sx={{fontSize: '16px', fontWeight: 500}}
          >
            {selectedValueForFunction}
          </Button>
          <Menu
            id="simple-menu"
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={() => handleClose(null)}
          >
            <MenuItem
              sx={{color: theme.palette.text.primary}}
              onClick={() => handleClose('Detektorer')}
            >
              Detektorer
            </MenuItem>
            <MenuItem
              sx={{color: theme.palette.text.primary}}
              onMouseEnter={handleSubmenuOpen}
            >
              Radioprestanda
              <Typography sx={{marginLeft: 'auto', color: theme.palette.text.primary}}>
                {' >'}
              </Typography>
              <Menu
                id="nested-menu"
                anchorEl={submenuAnchorEl}
                keepMounted
                open={Boolean(submenuAnchorEl)}
                onClose={handleSubmenuClose}
                sx={{
                  '& .MuiPaper-root': {
                    backgroundColor: theme.palette.background.paper,
                    position: 'absolute',
                    left: '100%',
                    top: 0,
                    marginLeft: '151px',
                  },
                }}
              >
                <MenuItem
                  sx={{color: theme.palette.text.primary}}
                  onClick={() => handleClose(SoftprioFeatureName.RadioQuality)}
                >
                  Radiokvalitet
                </MenuItem>
                <MenuItem
                  sx={{color: theme.palette.text.primary}}
                  onClick={() => handleClose(SoftprioFeatureName.UnderPerforming)}
                  // onMouseLeave={handleSubmenuClose}
                >
                  Underpresterande
                </MenuItem>
                <MenuItem
                  sx={{color: theme.palette.text.primary}}
                  onClick={() => handleClose(SoftprioFeatureName.HeatMap)}
                  onMouseLeave={handleSubmenuClose}
                >
                  Värmekarta
                </MenuItem>
              </Menu>
            </MenuItem>
            <MenuItem
              sx={{color: theme.palette.text.primary}}
              onClick={() => handleClose(SoftprioFeatureName.PowerSupply)}
            >
              Kraftförsörjning
            </MenuItem>
            <MenuItem
              sx={{color: theme.palette.text.primary}}
              onMouseEnter={handleMissingTagsSubmenuOpen}
            >
              Missade taggar
              <Typography sx={{marginLeft: 'auto', color: theme.palette.text.primary}}>
                {' >'}
              </Typography>
              <Menu
                id="nested-menu-missing-tags"
                anchorEl={missingTagsSubmenuAnchorEl}
                keepMounted
                open={Boolean(missingTagsSubmenuAnchorEl)}
                onClose={handleMissingTagsSubmenuClose}
                sx={{
                  '& .MuiPaper-root': {
                    backgroundColor: theme.palette.background.paper,
                    position: 'absolute',
                    left: '100%',
                    top: 0,
                    marginLeft: '151px',
                  },
                }}
              >
                <MenuItem
                  sx={{color: theme.palette.text.primary}}
                  onClick={() => {
                    setIsBackDropOpen(true);
                    handleClose(SoftprioFeatureName.MissingTags);
                  }}
                >
                  Diagram
                </MenuItem>
                <MenuItem
                  sx={{color: theme.palette.text.primary}}
                  onClick={() => {
                    setIsBackDropOpen(true);
                    handleClose(SoftprioFeatureName.DetectorSend);
                  }}
                >
                  Detector Resend Table
                </MenuItem>
                <MenuItem
                  sx={{color: theme.palette.text.primary}}
                  onClick={() => {
                    setIsBackDropOpen(true);
                    handleClose(SoftprioFeatureName.TramResend);
                  }}
                >
                  Tram Resend Table
                </MenuItem>
              </Menu>
            </MenuItem>
            <MenuItem
              sx={{color: theme.palette.text.primary}}
              onClick={() => handleClose(SoftprioFeatureName.LiveStatus)}
            >
              Live Status
            </MenuItem>{' '}
          </Menu>

          {/* Slider Switch with Icons added to the right */}
          <Box
            sx={{
              marginLeft: 'auto',
              marginRight: '2rem',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <LightMode sx={{color: theme.palette.warning.main, marginRight: 1}} />
            <FormControlLabel
              control={
                <Switch
                  checked={localTheme === 'dark'}
                  onChange={toggleLocalTheme}
                  color="warning" // Uses the warning color from the theme
                />
              }
              label={
                <DarkMode
                  sx={{
                    color: theme.palette.text.primary,
                    marginLeft: 1,
                  }}
                />
              }
              labelPlacement="end"
              sx={{
                '& .MuiTypography-root': {
                  marginRight: '8px',
                },
              }}
            />
          </Box>
        </Toolbar>
      </AppBar>

      <Stack direction="row" sx={{flexGrow: 1}}>
        {selectedValueForFunction === SoftprioFeatureName.LiveStatus && (
          <Sidebar
            functionName={selectedValueForFunction}
            isFullScreenForTramDetail={(isFullScreen) =>
              setIsFullScreenForTramDetail(isFullScreen)
            }
            onSelectedTram={(singleTram: VehicleStatus) => {
              setSingleProcessedTag(singleTram);
            }}
            fetchedTags={data!}
            processedTags={processedTags}
            activeTram={singleProcessedTag}
          />
        )}
        {pointGeoJsonDataV && !isFullScreenForTramDetail && (
          <StatusPage
            functionName={selectedValueForFunction}
            directionPoints={pointGeoJsonDataV}
            selectedTag={selectedTag}
            selectedCDC={selectedCdc}
            singleProcessedTag={singleProcessedTag}
            onAddDetector={addDetectorData}
            fetchedTags={data}
            isBackDropOpen={isBackDropOpen}
            closeBackDrop={setIsBackDropOpen}
            onClickTram={(tram: VehicleStatus) => {
              // console.log(tram);
              setSingleProcessedTag(tram);
            }}
            processedTags={processedTags}
          />
        )}
      </Stack>
    </Stack>
  );
};

export default StatusPageContainer;
